import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

export default function BlogSection() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((r) => setPosts(r.data.slice(0, 3)))
      .catch(() => {});
  }, []);

  if (!posts.length) return null;

  return (
    <section
      data-testid="blog-section"
      className="bg-liftile-cream-light py-20 md:py-28 text-liftile-matte-black"
    >
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-5"
        >
          <div>
            <div className="heading-eyebrow mb-3">Knowledge Center</div>
            <h2 className="display-2">
              Construction <span className="text-liftile-orange">Insights</span>
            </h2>
          </div>
          <Link
            to="/insights"
            data-testid="view-all-blog"
            className="font-heading text-sm uppercase tracking-wider text-liftile-orange flex items-center gap-2 hover:gap-3 transition-all"
          >
            View All Articles <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {posts.map((post, i) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="group h-full flex flex-col bg-white rounded-2xl overflow-hidden shadow-[0_4px_18px_rgba(0,0,0,0.05)] hover:shadow-[0_18px_45px_-12px_rgba(0,0,0,0.18)] border border-transparent hover:border-liftile-orange/30 hover:-translate-y-1.5 transition-all duration-500"
            >
              <Link
                to={`/insights/${post.slug}`}
                data-testid={`blog-card-${post.slug}`}
                className="block aspect-[16/10] overflow-hidden"
              >
                <img
                  src={post.cover_image}
                  alt={post.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                />
              </Link>
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-liftile-matte-black/55 mb-3">
                  <span className="text-liftile-orange font-heading uppercase tracking-[0.18em] font-semibold">
                    {post.category}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {post.read_time}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {formatDate(post.published_at)}
                  </span>
                </div>
                <Link to={`/insights/${post.slug}`}>
                  <h3 className="font-heading text-lg leading-snug font-semibold text-liftile-matte-black group-hover:text-liftile-orange transition-colors duration-300 line-clamp-3">
                    {post.title}
                  </h3>
                </Link>
                <p className="font-body text-sm text-liftile-matte-black/70 mt-3 leading-relaxed line-clamp-3 flex-1">
                  {post.excerpt}
                </p>
                <Link
                  to={`/insights/${post.slug}`}
                  aria-label={`Read the full article: ${post.title}`}
                  className="mt-5 inline-flex items-center gap-2 font-heading text-xs uppercase tracking-[0.2em] font-semibold text-liftile-orange self-start hover:gap-3 transition-all duration-300"
                >
                  Read Article <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
